import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useAppSelector } from '@/redux/hooks';
import { selectUserPreferences } from '@/redux/selectors/authSelectors';
import { FocusVersionScheduling } from '@/redux/slices/slice_types';
import { LicenseChip, StyledPaper } from '@/components/_common/Table/Calendar/CalendarComponents/common';
import FocusImage from '@/components/Focus/common/FocusImage';
import FocusImageContainer from '@/components/Focus/common/FocusImageContainer';
import FocusTitleHierarchyBadge from '@/components/Focus/common/FocusTitleHierarchyBadge';
import VersionNameChip from '@/components/Focus/common/VersionNameChip';
import { useFocusTheme } from '@/components/Focus/configs/useFocusTheme.hook';

const DataCell: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const theme = useFocusTheme();
  return (
    <Grid item container alignItems="stretch" xs={6}>
      <StyledPaper>
        <Typography sx={{ fontSize: '0.857rem', fontWeight: 600, color: theme.customOverrides.Calendar.textSecondary }}>{label}</Typography>
        <Typography sx={{ fontSize: '1.143rem' }}>{children}</Typography>
      </StyledPaper>
    </Grid>
  );
};

interface Props {
  details: FocusVersionScheduling;
  showPoster?: boolean;
}

const AvailDetailsMainInfo: React.FC<Props> = ({ details, showPoster }) => {
  const theme = useFocusTheme();
  const { date_format, time_format } = useAppSelector(selectUserPreferences);

  return (
    <Grid container spacing={0.5}>
      <Grid item xs={12}>
        <StyledPaper big sx={{ mb: 0.5 }}>
          {!!details.title?.season_sequence_order && !!details.title?.episode_sequence_order && (
            <Box sx={{ mb: 2 }}>
              <FocusTitleHierarchyBadge seasonOrder={details.title.season_sequence_order} episodeOrder={details.title.episode_sequence_order} />
            </Box>
          )}
          <Typography sx={{ mb: 0.5, fontSize: '1.357rem', lineHeight: '1.714rem' }}>{details.title?.title_name}</Typography>
          <Typography sx={{ mb: 2, fontSize: '1.143rem', fontWeight: 700, color: theme.customOverrides.Calendar.highlight }}>
            {details.title?.title_name_supplemental}
          </Typography>
          <Grid container spacing={1}>
            <Grid item>
              {details.license_types.map((l) => (
                <LicenseChip key={l} label={l.toLocaleUpperCase()} variant="outlined" />
              ))}
            </Grid>
            <Grid item>
              <VersionNameChip label={details.title?.availed_type} />
            </Grid>
          </Grid>

          {showPoster && (
            <FocusImageContainer sx={{ mt: 2 }}>
              <FocusImage entity={details} />
            </FocusImageContainer>
          )}
        </StyledPaper>
      </Grid>

      <DataCell label="Date">{date_format ? format(new Date(details.term_start), date_format) : null}</DataCell>
      <DataCell label="Alpha ID">{details.title?.client_ids_supplemental.alpha_id}</DataCell>
      <DataCell label="Time">{time_format ? format(new Date(details.term_start), time_format) : null}</DataCell>
      <DataCell label="RL ID">{details.title?.client_ids_supplemental.rightsline_id}</DataCell>
    </Grid>
  );
};

export default AvailDetailsMainInfo;
